import { format } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { HolidaySchedule, UpcomingIssue } from 'shared_server_client/types/schedule'

// Constants
const HOLIDAY_TYPE = 'Public holiday'

// Calendar Colors
const CALENDAR_COLORS = {
  ERROR: 'red',
  FUTURE_SEND: '#72cac2',
  FUTURE_OVERRIDE: '#e65100',
  PAST_SEND_SUCCESS: '#ff9800',
  PAST_SEND_OTHER: '#ff5722',
  HOLIDAY_BG: 'white',
  HOLIDAY_BORDER: 'white'
} as const

const TIME_FORMAT = 'h:mm a'

// Issue Status Types
const ISSUE_STATUS = {
  SENT: 'Sent',
  DELIVERED: 'Delivered',
  COMPLETE: 'Complete',
  GENERATING: 'Generating',
  CANCELED: 'Canceled'
} as const

// Issue Titles
const ISSUE_TITLES = {
  SCHEDULE_PAUSED: 'Schedule is paused',
  NEWSLETTER_SENT: 'Newsletter Sent',
  NEWSLETTER_DELIVERED: 'Newsletter Delivered',
  WAS_GENERATING: 'Was Generating',
  PAST_SEND: 'Past Send',
  CANCELED: 'Canceled',
  SKIPPED: 'Skipped',
  SCHEDULED_CONTENT: 'Scheduled Content'
} as const

export const mapIssuesToSchedules = (issues: UpcomingIssue[], hasScheduledContentAccess: boolean): any[] => {
  return issues.map((issue, i) => {
    const now = new Date()
    
    // Use the proper date field - prefer issue_date if available, otherwise use date
    let issueDate
    if (issue.issue_date && issue.issue_date instanceof Date) {
      issueDate = issue.issue_date
    } else if (issue.issue_date) {
      issueDate = new Date(issue.issue_date)
    } else if (issue.date) {
      issueDate = new Date(issue.date)
    } else {
      issueDate = new Date() // Use current date as last resort
    }
    
    // Validate the date, but don't skip - use fallback
    if (isNaN(issueDate.getTime())) {
      issueDate = new Date() // Use current date as fallback
    }
    
    const isPastSend = issueDate < now
    const baseTitle = getIssueTitle(issue, isPastSend, hasScheduledContentAccess)
    const timeStr = format(issueDate, TIME_FORMAT)
    const title = baseTitle ? `${timeStr} - ${baseTitle}` : timeStr
    const updatedIssue = {...issue, hasScheduledContent: baseTitle === ISSUE_TITLES.SCHEDULED_CONTENT}
    
    // Create unique ID using issue ID if available, otherwise use combination of date and index
    const uniqueId = issue.id || issue.holiday_id || `${issueDate.getTime()}_${i}`
    
    return {
      id: uniqueId.toString(),
      body: { updatedIssue },
      borderColor: getBorderColor(issue, title),
      calendarId: '0',
      title,
      category: 'time',
      dueDateClass: '',
      start: issueDate.toISOString(),
      end: issueDate.toISOString(),
      isvisible: true,
    }
  })
}

const getIssueTitle = (issue: any, isPastSend: boolean, hasScheduledContentAccess: boolean): string => {
  const schedule = issue.schedule
  
  // Original logic: !schedule.is_active ? 'Schedule is paused' : issue.is_active ? onMessage : ''
  if (!schedule.is_active) {
    return ISSUE_TITLES.SCHEDULE_PAUSED
  }
  
  if (issue.is_active) {
    // For active issues, we now differentiate between past and future sends (NEW FEATURE)
    if (isPastSend) {
      return getPastSendTitle(issue.status)
    } else {
      // Original onMessage logic for future sends
      const onMessage = !hasScheduledContentAccess ? '' : issue.hasScheduledContent ? ISSUE_TITLES.SCHEDULED_CONTENT : ''
      return onMessage
    }
  }
  
  // If schedule is active but issue is not active (original: return empty string)
  return ''
}

const getPastSendTitle = (status: string): string => {
  switch (status) {
    case ISSUE_STATUS.SENT:
    case ISSUE_STATUS.COMPLETE:
      return ISSUE_TITLES.NEWSLETTER_SENT
    case ISSUE_STATUS.DELIVERED:
      return ISSUE_TITLES.NEWSLETTER_DELIVERED
    case ISSUE_STATUS.GENERATING:
      return ISSUE_TITLES.WAS_GENERATING
    default:
      return ISSUE_TITLES.PAST_SEND
  }
}

export const mapSchedules = (issues: UpcomingIssue[], holidays: any[], hasScheduledContentAccess: boolean): any[] => {
  const currentHolidays = mapHolidaysToSchedules(holidays)
  let schedules = mapIssuesToSchedules(issues, hasScheduledContentAccess)
  schedules = [...currentHolidays, ...schedules]
  schedules = schedules.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
  return schedules
}

export const mapHolidaysToSchedules = (currentHolidays: HolidaySchedule[]): any[] => {
  currentHolidays = currentHolidays.filter((holiday) => holiday.description === HOLIDAY_TYPE)
  return currentHolidays.map((holiday, i) => {
    return {
      id: i.toString(),
      bgColor: CALENDAR_COLORS.HOLIDAY_BG,
      borderColor: CALENDAR_COLORS.HOLIDAY_BORDER,
      calendarId: '0',
      title: holiday.title,
      category: 'allday',
      isvisible: true,
      dueDateClass: '',
      start: holiday.startDate,
      end: holiday.endDate,
    }
  })
}

export const getMonthYear = (date: Date): string => {
  return format(date, 'MMMM yyyy')
}

export const normalizeIssues = (issues: UpcomingIssue[], timezone: string): UpcomingIssue[] => {
  return issues.filter(i => {
    // Filter out issues without valid dates first
    return !!i.date
  }).map((i: UpcomingIssue) => {
    // Handle date conversion more carefully
    let issueDate
    try {
      // If date is already a Date object, use it directly
      if (i.date && typeof i.date === 'object' && (i.date as any) instanceof Date) {
        issueDate = i.date as Date
      } else {
        // Try to convert string date to proper format
        const dateValue = new Date(i.date)
        if (isNaN(dateValue.getTime())) {
          return null // Skip this issue rather than use current date
        } else {
          issueDate = utcToZonedTime(dateValue, timezone)
        }
      }
    } catch (error) {
      return null // Skip this issue rather than use current date
    }
    
    return {
      ...i,
      id: i.holiday_id,
      issue_date: issueDate,
    }
  }).filter(Boolean) // Remove null entries
}

export const normalizeHolidays = (holidays: HolidaySchedule[], timezone: string): HolidaySchedule[] => {
  return holidays.map((h: HolidaySchedule) => ({
    ...h,
    startDate: isoToDateString(h.startDate),
    endDate: isoToDateString(h.endDate),
  }))
}

const getBorderColor = (issue, title) => {
  const schedule = issue.schedule
  const now = new Date()
  const issueDate = new Date(issue.date || issue.issue_date)
  const isPastSend = issueDate < now
  
  // Return error color for inactive, paused schedules, or canceled issues
  if (!schedule.is_active || !issue.is_active || title === ISSUE_TITLES.CANCELED || title === ISSUE_TITLES.SKIPPED) {
    return CALENDAR_COLORS.ERROR
  }
  
  // Return different colors based on status and timing
  if (isPastSend) {
    return isSuccessfulPastSend(title) ? CALENDAR_COLORS.PAST_SEND_SUCCESS : CALENDAR_COLORS.PAST_SEND_OTHER
  } else {
    return issue.source === 'override' ? CALENDAR_COLORS.FUTURE_OVERRIDE : CALENDAR_COLORS.FUTURE_SEND
  }
}

const isSuccessfulPastSend = (title: string): boolean => {
  return title === ISSUE_TITLES.NEWSLETTER_SENT || title === ISSUE_TITLES.NEWSLETTER_DELIVERED
}

export const isoToDateString = (isoDate: string): string => {
  return isoDate.substring(0, 10)
}
